import RubricsPortfolioContext from '@/lib/contexts/RubricsPortfolioContext';
import { type HTMLButtonProps } from '@/lib/types';
import { alpha, Button, type ButtonProps } from '@mui/material';
import { useContext } from 'react';
import styled from '@emotion/styled';
import {
  Add as AddIcon,
  Attachment as AttachmentIcon,
} from '@mui/icons-material';
import stripProps from '@/lib/utils/stripProps';
import addHighContrastStyles from '@/lib/styling/addHighContrastStyles';
import { FormattedMessage } from 'react-intl';
import PortfolioIsReadOnlyContext from '@/lib/contexts/PortfolioIsReadOnlyContext';
import ButtonUnreadCoachCommentsBadge from './ButtonUnreadCoachCommentsBadge';

type StyledButtonProps = ButtonProps & {
  hasLabel: boolean;
};

const StyledButton = styled(
  stripProps(Button, 'hasLabel') as typeof Button,
)<StyledButtonProps>(
  ({ hasLabel, theme }) => ({
    padding: hasLabel ? '6px 12px' : 6,
    minWidth: 0,
    maxWidth: '100%',
    backgroundColor: alpha(theme.palette.primary.main, 0.2),
    borderRadius: 18,
    ':hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.3),
    },
    span: {
      marginLeft: 6,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  }),
  addHighContrastStyles(({ theme }) => ({
    backgroundColor: `rgba(0, 0, 0, ${theme.palette.isDark ? 0.2 : 0.1})`,
    border: `2px solid ${theme.palette.divider}`,
    borderRadius: 20,
    ':hover': {
      backgroundColor: `rgba(0, 0, 0, ${theme.palette.isDark ? 0.5 : 0.2})`,
    },
  })),
);

type AttachmentsButtonProps = Pick<HTMLButtonProps, 'onClick'> & {
  levelId: number;
};

const AtachmentsButton = ({ levelId, ...props }: AttachmentsButtonProps) => {
  const [portfolio] = useContext(RubricsPortfolioContext);

  const proofLinks = Object.values(portfolio.proofLinks[levelId] ?? {});
  const proofImages = Object.values(portfolio.proofImages?.[levelId] ?? {});
  const proofExternalFiles = Object.values(
    portfolio.proofExternalFiles?.[levelId] ?? {},
  );
  const levelProgressNotes = Object.values(
    portfolio.levelProgressNotes?.[levelId] ?? {},
  );
  const levelProgressCoachComments = Object.values(
    portfolio.levelProgressCoachComments?.[levelId] ?? {},
  );

  const allAttachmentsCount =
    proofLinks.length +
    proofImages.length +
    proofExternalFiles.length +
    levelProgressNotes.length +
    levelProgressCoachComments.length;

  const portfolioIsReadonly = useContext(PortfolioIsReadOnlyContext);

  if (allAttachmentsCount === 0 && portfolioIsReadonly) {
    return null;
  }

  const unreadCoachCommentsCount = levelProgressCoachComments.filter(
    coachComment => !coachComment.isRead,
  ).length;

  return (
    <ButtonUnreadCoachCommentsBadge
      unreadCoachCommentsCount={
        portfolioIsReadonly ? 0 : unreadCoachCommentsCount
      }
    >
      <StyledButton hasLabel={allAttachmentsCount > 0} {...props}>
        {allAttachmentsCount === 0 ? (
          <AddIcon />
        ) : (
          <>
            <AttachmentIcon />
            <span>
              <FormattedMessage
                id={`portfolio.level.attachments.button.label.${allAttachmentsCount > 1 ? 'plural' : 'singular'}`}
                values={{
                  attachmentsCount: allAttachmentsCount,
                }}
              />
            </span>
          </>
        )}
      </StyledButton>
    </ButtonUnreadCoachCommentsBadge>
  );
};

export default AtachmentsButton;
